import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { personaApi } from '@/lib/api/persona';
import { fontesApi, type Fonte } from '@/lib/api/oto';
import { formatarTamanho, type Anexo } from '@/lib/api/anexos';
import { useBairros, type BairroView } from '@/lib/hooks/useBairros';
import { useTemas } from '@/lib/hooks/useTemas';
import { Anexos } from '@/modules/gab/anexos/Anexos';
import { useAuthStore } from '@/lib/stores/authStore';
import { toast } from '@/lib/stores/toastStore';
import { Info } from '@/design-system/Info';

const TONS = [
  { v: 'Equilibrado', l: 'Equilibrado (técnico e neutro)' },
  { v: 'Assertivo', l: 'Assertivo (combativo, focado em fiscalização)' },
  { v: 'Popular', l: 'Popular (linguagem direta e acolhedora)' },
  { v: 'Formal', l: 'Formal (protocolar e institucional)' },
];

/** Uma fala do diálogo. Texto puro; o link, quando existe, é um `Link` real. */
interface Mensagem {
  me: boolean;
  txt: string;
  link?: { para: string; rotulo: string };
}

const ABERTURA: Mensagem = {
  me: false,
  txt:
    'Sou o OTO. Aqui eu calibro a sua persona com o que você me contar: diga suas ' +
    'prioridades, o que você evita, ou peça "ler meus dados" para eu repetir o que ' +
    'já está carregado. Não leio arquivos nem a internet.',
};

type Nivel = 'good' | 'warn' | 'crit';

interface Abrangencia {
  nivel: Nivel;
  rotulo: string;
  uso: string;
}

const semAcento = (s: string) =>
  s
    .normalize('NFD')
    // Os combinantes por escape, e não os caracteres literais: escritos
    // literalmente eles não sobrevivem a normalização de editor nem a
    // formatador, e o replace viraria um no-op silencioso.
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

/**
 * Enquanto os territórios do mandato não chegaram não há contra o que conferir.
 * Classificar agora cairia no ramo `crit` — "não corresponde a nenhum
 * território" — que é afirmação sem lastro: ninguém olhou território nenhum.
 */
const CONFERINDO: Abrangencia = {
  nivel: 'warn',
  rotulo: 'conferindo',
  uso: 'Conferindo a abrangência contra os territórios cadastrados no mandato...',
};

/**
 * Abrangência da fonte, a partir do escopo que o usuário declarou.
 *
 * O protótipo adivinhava isto do NOME do arquivo (`/fortaleza|datamar|munic/`,
 * `/eua|gallup|pew/`, proto:1523). Aqui o insumo é o campo `escopo` da fonte —
 * metadado informado por quem cadastrou — conferido contra os territórios reais
 * do mandato. Sem escopo declarado não há classificação: fica `warn` e pede o
 * dado, em vez de chutar um território e dizer que a fonte "entra" nele.
 */
export function classificarAbrangencia(
  escopo: string | undefined,
  bairros: BairroView[],
  territoriosCarregando = false,
): Abrangencia {
  if (territoriosCarregando) return CONFERINDO;

  const declarado = (escopo || '').trim();
  if (!declarado) {
    return {
      nivel: 'warn',
      rotulo: 'a definir',
      uso: 'Informe a abrangência em Fontes & conhecimento para o OTO saber onde aplicá-la.',
    };
  }

  const alvo = semAcento(declarado);

  const bairro = bairros.find((b) => semAcento(b.n) === alvo);
  if (bairro) {
    return {
      nivel: 'good',
      rotulo: bairro.n,
      uso: `Entra nas estratégias de ${bairro.n}: é a fonte mais específica que existe.`,
    };
  }

  const municipio = bairros.find((b) => semAcento(b.municipio) === alvo);
  if (municipio) {
    return {
      nivel: 'good',
      rotulo: municipio.municipio,
      uso: `Entra nas estratégias de todos os territórios de ${municipio.municipio}.`,
    };
  }

  const uf = bairros.find((b) => semAcento(b.uf) === alvo);
  if (uf) {
    return {
      nivel: 'good',
      rotulo: uf.uf,
      uso: `Contexto estadual (${uf.uf}): vale menos que dado do território.`,
    };
  }

  return {
    nivel: 'crit',
    rotulo: declarado,
    uso: 'Não corresponde a nenhum território, município ou UF cadastrados: fica arquivada como referência.',
  };
}

const plural = (n: number, singular: string, plural_: string) =>
  `${n} ${n === 1 ? singular : plural_}`;

/** Separa "Creche e Saúde" ou "creche, saúde" nos itens que a pessoa disse. */
const itensDitos = (txt: string) =>
  txt
    .split(/\s+e\s+|,|;/i)
    .map((s) => s.trim().replace(/[.!]+$/, ''))
    .filter(Boolean);

export function PerfilPage() {
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();
  const { bairros, isLoading: bairrosCarregando } = useBairros();
  const { temas, isLoading: temasCarregando } = useTemas();

  const { data: persona, isLoading } = useQuery({
    queryKey: ['persona'],
    queryFn: () => personaApi.get(token),
    enabled: !!token,
  });

  const { data: fontes = [] } = useQuery({
    queryKey: ['fontes', {}],
    queryFn: () => fontesApi.list(token),
    enabled: !!token,
  });

  // Rascunho editável, hidratado a partir da persona salva no banco
  const [tom, setTom] = useState('Equilibrado');
  const [publico, setPublico] = useState('');
  const [bandeiras, setBandeiras] = useState<string[]>([]);
  const [valores, setValores] = useState<string[]>([]);
  const [limites, setLimites] = useState<string[]>([]);

  const [novaBandeira, setNovaBandeira] = useState('');
  const [novoValor, setNovoValor] = useState('');
  const [novoLimite, setNovoLimite] = useState('');

  useEffect(() => {
    if (persona) {
      setTom(persona.tom);
      setPublico(persona.publico);
      setBandeiras(persona.bandeiras || []);
      setValores(persona.valores || []);
      setLimites(persona.limites || []);
    }
  }, [persona]);

  const updateMutation = useMutation({
    mutationFn: (data: any) => personaApi.update(data, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['persona'] });
      toast('Persona salva e sincronizada com os agentes do OTO.');
    },
    onError: (err: any) => {
      toast(err.response?.data?.message || 'Erro ao salvar a persona.');
    },
  });

  const handleSalvar = () => {
    updateMutation.mutate({ tom, publico, bandeiras, valores, limites });
  };

  const adicionar = (
    valor: string,
    lista: string[],
    setLista: (v: string[]) => void,
    limpar: () => void,
  ) => {
    const item = valor.trim();
    if (!item || lista.includes(item)) return;
    setLista([...lista, item]);
    limpar();
  };

  const remover = (item: string, lista: string[], setLista: (v: string[]) => void) => {
    setLista(lista.filter((i) => i !== item));
  };

  // ----- Fontes: o anexo pertence a uma fonte -----
  const [fonteSel, setFonteSel] = useState<string | null>(null);
  const fonteAtiva = fontes.find((f) => f.id === fonteSel) || null;

  // ----- Diálogo de calibragem com o OTO -----
  const [chatInput, setChatInput] = useState('');
  const [digitando, setDigitando] = useState(false);
  const [chatMessages, setChatMessages] = useState<Mensagem[]>([ABERTURA]);
  const relogio = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (relogio.current) clearTimeout(relogio.current); }, []);

  const dizer = (m: Mensagem) => setChatMessages((prev) => [...prev, m]);

  /**
   * Responde ao que a pessoa escreveu — e, quando for o caso, MUDA o rascunho.
   *
   * Cada ramo faz uma coisa verificável: acrescenta à lista ao lado, repete
   * números já carregados, ou encaminha para a tela que de fato gera o que foi
   * pedido. Não existe ramo que produza análise de território: a geração vive
   * em Estratégias, com o modelo de linguagem do servidor. Texto de diagnóstico
   * escrito aqui seria invenção com aparência de leitura.
   */
  const responder = (q: string): Mensagem => {
    const prioridades = q.match(/^minhas prioridades s[ãa]o\s+(.+)$/i);
    const adicioneBandeira = q.match(/^adicione\s+(.+?)\s+[àa]s minhas bandeiras\.?$/i);
    const declaracaoBandeira = prioridades || adicioneBandeira;

    if (declaracaoBandeira) {
      const itens = itensDitos(declaracaoBandeira[1] ?? '');
      const novos = itens.filter((i) => !bandeiras.includes(i));
      if (novos.length) setBandeiras([...bandeiras, ...novos]);
      return {
        me: false,
        txt: novos.length
          ? `Acrescentei às bandeiras: ${novos.join(', ')}. Ainda não foi salvo; clique em "Salvar Persona" para gravar.`
          : `${itens.join(', ')} já estava nas bandeiras. Nada mudou.`,
      };
    }

    const evita = q.match(/^eu evito\s+(.+)$/i);
    if (evita) {
      const itens = itensDitos(evita[1] ?? '');
      const novos = itens.filter((i) => !limites.includes(i));
      if (novos.length) setLimites([...limites, ...novos]);
      return {
        me: false,
        txt: novos.length
          ? `Registrei nos limites: ${novos.join(', ')}. Ainda não foi salvo — clique em "Salvar Persona" para gravar.`
          : `${itens.join(', ')} já estava nos limites. Nada mudou.`,
      };
    }

    if (/^ler meus dados\.?$/i.test(q.trim())) {
      const semEscopo = fontes.filter((f) => !(f.escopo || '').trim()).length;
      return {
        me: false,
        txt:
          `Persona salva: ${persona?.completude ?? 0}% preenchida, versão ${persona?.versao ?? 1}. ` +
          `Tom "${tom}". Público prioritário: ${publico.trim() || 'não informado'}. ` +
          `No rascunho desta tela: ${plural(bandeiras.length, 'bandeira', 'bandeiras')}, ` +
          `${plural(valores.length, 'valor', 'valores')} e ` +
          `${plural(limites.length, 'limite', 'limites')}. ` +
          `${plural(fontes.length, 'fonte cadastrada', 'fontes cadastradas')}, ` +
          `${semEscopo} sem abrangência informada. ` +
          `${plural(bairros.length, 'território', 'territórios')} no mandato. ` +
          'É tudo o que esta tela carregou — nenhum arquivo foi lido para montar este resumo.',
      };
    }

    const estrategia = q.match(/^estrat[ée]gia\s+(?:para|de)\s+(.+)$/i);
    if (estrategia) {
      const pedido = (estrategia[1] ?? '').trim();
      const territorio = bairros.find((b) => semAcento(pedido).includes(semAcento(b.n)));
      return {
        me: false,
        txt:
          'Estratégia não sai daqui: este painel calibra a persona. A geração acontece em ' +
          'Estratégias, onde o OTO usa a persona salva, o território e o tema. ' +
          (territorio
            ? `Território reconhecido no pedido: ${territorio.n}.`
            : `Não reconheci um território cadastrado em "${pedido}" — a geração exige um dos territórios do mandato.`),
        link: { para: '/estrategias', rotulo: 'Abrir Estratégias' },
      };
    }

    return {
      me: false,
      txt:
        'Não sei responder isso aqui. O que este painel faz: "minhas prioridades são ...", ' +
        '"adicione <tema> às minhas bandeiras", "eu evito ...", "ler meus dados" e ' +
        '"estratégia para <território>".',
    };
  };

  const handleEnviarMensagem = (msgCustom?: string) => {
    const q = (msgCustom ?? chatInput).trim();
    if (!q || digitando) return;

    dizer({ me: true, txt: q });
    setChatInput('');
    setDigitando(true);

    relogio.current = setTimeout(() => {
      setDigitando(false);
      dizer(responder(q));
    }, 250);
  };

  /**
   * As sugestões saem do vocabulário real do mandato: temas são as categorias
   * de solicitação (`useTemas`) e territórios são os bairros cadastrados
   * (`useBairros`). O protótipo cravava "saúde e creche", "Messejana" e
   * "Aldeota" (proto:1557) — num mandato de outra cidade, as nove sugestões
   * falavam de bairros que não existem lá.
   *
   * "Confirmar", a nona do protótipo, ficou de fora: ela confirmava a persona
   * "extraída" do anexo, e não há extração nenhuma para confirmar.
   */
  const sugestoes = [
    temas[0] && temas[1] && `Minhas prioridades são ${temas[0]} e ${temas[1]}`,
    'Ler meus dados',
    temas[2] && `Adicione ${temas[2]} às minhas bandeiras`,
    'Eu evito pautas morais',
    'Eu evito pautas polarizantes',
    bairros[0] && `Estratégia para ${bairros[0].n}`,
    bairros[1] && `Estratégia para ${bairros[1].n}`,
    temas[3] && bairros[0] && `Estratégia de ${temas[3]} em ${bairros[0].n}`,
    temas[4] && `Estratégia de ${temas[4]}`,
  ].filter((s): s is string => typeof s === 'string');

  const registrarAnexo = (fonte: Fonte, anexo: Anexo) => {
    const abrangencia = classificarAbrangencia(fonte.escopo, bairros, bairrosCarregando);
    dizer({ me: true, txt: `📎 ${anexo.nome}` });
    dizer({
      me: false,
      txt:
        `"${anexo.nome}" (${formatarTamanho(anexo.tamanho)}) ficou guardado na fonte ` +
        `"${fonte.nome}" e pode ser baixado a qualquer momento. O sistema não lê o conteúdo ` +
        'do arquivo, então não há nada a deduzir dele: o que o OTO usa da fonte é o resumo e ' +
        'o uso que você escreve em Fontes & conhecimento. ' +
        `Abrangência: ${abrangencia.rotulo} — informada por você, não descoberta no arquivo.`,
    });
  };

  return (
    <div className="page" id="p-perfil">
      {/* Header */}
      <div className="head">
        <div>
          <h1 style={{ color: '#B26A00' }}>Perfil &amp; OTO</h1>
          <p>
            {/*
              proto:1502 promete que o OTO constrói a persona "com o que você
              conta, o que anexa e o que o sistema já sabe" — nada no sistema lê
              o conteúdo de um anexo, não há extrator de PDF ou DOCX no
              servidor. E o texto que estava aqui dizia que o OTO "coordena os
              agentes de IA": `oto.agente` não é lido por serviço nenhum. O que
              é verdade está na frase abaixo — tom, público, bandeiras, valores
              e limites entram no contexto do modelo em `pedirAoModelo()`.
            */}
            Construa e refine sua identidade estratégica em conversa. Tom de voz, público
            prioritário, bandeiras, valores e limites salvos aqui são o contexto que o OTO usa ao
            gerar cada estratégia.
          </p>
        </div>
        <div className="act">
          <button
            className="btn pri"
            onClick={handleSalvar}
            disabled={updateMutation.isPending || isLoading}
          >
            {updateMutation.isPending ? 'Salvando...' : 'Salvar Persona'}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="empty">Buscando identidade da persona...</div>
      ) : (
        <div className="grid g21">
          {/* Painel Esquerdo: Diálogo de Calibragem */}
          <div className="card" style={{ display: 'flex', flexDirection: 'column', minHeight: 460 }}>
            <h2>Diálogo Estratégico com o OTO</h2>

            <div className="msgbox" style={{ flex: 1, maxHeight: 360, overflowY: 'auto', marginBottom: 12 }}>
              {chatMessages.map((m, idx) => (
                <div
                  key={idx}
                  className={`msg ${m.me ? 'me' : ''}`}
                  style={!m.me ? { background: 'var(--warn-soft)', maxWidth: '95%' } : {}}
                >
                  {!m.me && <b style={{ color: '#B26A00' }}>OTO</b>}
                  <div style={{ marginTop: 4, lineHeight: 1.5 }}>{m.txt}</div>
                  {m.link && (
                    <div style={{ marginTop: 6 }}>
                      <Link className="btn sm" to={m.link.para}>
                        {m.link.rotulo}
                      </Link>
                    </div>
                  )}
                </div>
              ))}
              {digitando && (
                <div className="msg" style={{ background: 'var(--warn-soft)', maxWidth: '95%' }}>
                  <b style={{ color: '#B26A00' }}>OTO</b>
                  <div style={{ marginTop: 4, color: 'var(--ink3)' }}>digitando...</div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                className="in"
                placeholder="Fale sobre suas prioridades, valores ou peça uma estratégia..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleEnviarMensagem()}
              />
              <button className="btn pri" onClick={() => handleEnviarMensagem()}>
                Enviar
              </button>
            </div>

            <div className="filters" style={{ marginTop: 10, flexWrap: 'wrap' }}>
              {sugestoes.map((sug) => (
                <span
                  key={sug}
                  className="chip neutral chipbtn"
                  onClick={() => handleEnviarMensagem(sug)}
                >
                  {sug}
                </span>
              ))}
            </div>

            {!temasCarregando && !bairrosCarregando && sugestoes.length < 9 && (
              <div className="note warn" style={{ fontSize: 11, marginTop: 8 }}>
                {sugestoes.length} das 9 sugestões: as demais dependem dos temas e territórios
                cadastrados neste mandato (Configurações › Tipos e Territórios). Nenhum exemplo
                de outro município entra no lugar.
              </div>
            )}
          </div>

          {/* Painel Direito: Persona editável */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="card" style={{ borderColor: '#B26A00' }}>
              <h2 style={{ color: '#B26A00' }}>Persona Ativa</h2>

              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '4px 0 14px' }}>
                <b style={{ fontSize: 26, color: 'var(--good)' }}>{persona?.completude ?? 0}%</b>
                <span style={{ fontSize: 12, color: 'var(--ink3)' }}>da identidade preenchida</span>
              </div>

              <div className="field" style={{ marginBottom: 12 }}>
                <label>Tom de voz da IA</label>
                <select
                  className="sel"
                  aria-label="Tom de voz da IA"
                  value={tom}
                  onChange={(e) => setTom(e.target.value)}
                >
                  {TONS.map((t) => (
                    <option key={t.v} value={t.v}>
                      {t.l}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field" style={{ marginBottom: 12 }}>
                <label>Público alvo prioritário</label>
                <input
                  type="text"
                  className="in"
                  placeholder="Ex: Famílias de baixa renda, pequenos comerciantes..."
                  value={publico}
                  onChange={(e) => setPublico(e.target.value)}
                />
              </div>

              <div className="field" style={{ marginBottom: 12 }}>
                <label>Bandeiras / causas principais</label>
                <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <input
                    type="text"
                    className="in sm"
                    placeholder="Ex: Saúde pública, Mobilidade..."
                    value={novaBandeira}
                    onChange={(e) => setNovaBandeira(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === 'Enter' &&
                      (e.preventDefault(),
                      adicionar(novaBandeira, bandeiras, setBandeiras, () => setNovaBandeira('')))
                    }
                  />
                  <button
                    type="button"
                    className="btn sm pri"
                    onClick={() => adicionar(novaBandeira, bandeiras, setBandeiras, () => setNovaBandeira(''))}
                  >
                    Adicionar
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {bandeiras.map((b) => (
                    <span
                      key={b}
                      data-testid="bandeira"
                      className="chip good"
                      style={{ cursor: 'pointer' }}
                      onClick={() => remover(b, bandeiras, setBandeiras)}
                      title={`Remover bandeira ${b}`}
                    >
                      {b} ✕
                    </span>
                  ))}
                </div>
              </div>

              <div className="field" style={{ marginBottom: 12 }}>
                <label>Valores do mandato</label>
                <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <input
                    type="text"
                    className="in sm"
                    placeholder="Ex: Transparência radical, Fiscalização..."
                    value={novoValor}
                    onChange={(e) => setNovoValor(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === 'Enter' &&
                      (e.preventDefault(),
                      adicionar(novoValor, valores, setValores, () => setNovoValor('')))
                    }
                  />
                  <button
                    type="button"
                    className="btn sm pri"
                    onClick={() => adicionar(novoValor, valores, setValores, () => setNovoValor(''))}
                  >
                    Adicionar
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {valores.map((v) => (
                    <span
                      key={v}
                      data-testid="valor"
                      className="chip neutral"
                      style={{ cursor: 'pointer' }}
                      onClick={() => remover(v, valores, setValores)}
                      title={`Remover valor ${v}`}
                    >
                      {v} ✕
                    </span>
                  ))}
                </div>
              </div>

              <div className="field">
                <label>Temas a evitar (limites de IA Segura)</label>
                <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <input
                    type="text"
                    className="in sm"
                    placeholder="Ex: Polêmicas nacionais, Futebol, Religião..."
                    value={novoLimite}
                    onChange={(e) => setNovoLimite(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === 'Enter' &&
                      (e.preventDefault(),
                      adicionar(novoLimite, limites, setLimites, () => setNovoLimite('')))
                    }
                  />
                  <button
                    type="button"
                    className="btn sm pri"
                    onClick={() => adicionar(novoLimite, limites, setLimites, () => setNovoLimite(''))}
                  >
                    Adicionar
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {limites.map((l) => (
                    <span
                      key={l}
                      data-testid="limite"
                      className="chip crit"
                      style={{ cursor: 'pointer' }}
                      onClick={() => remover(l, limites, setLimites)}
                      title={`Remover limite ${l}`}
                    >
                      {l} ✕
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Fontes externas e escopo (proto:1557) */}
            <div className="card">
              <h2>Fontes externas e escopo</h2>
              <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '4px 0 10px' }}>
                Cada fonte cadastrada em{' '}
                <Link to="/fontes" style={{ color: 'var(--accent)' }}>
                  Fontes &amp; conhecimento
                </Link>{' '}
                com a abrangência que você declarou. Escolha uma para anexar o arquivo original.
              </p>

              {fontes.length === 0 && (
                <div className="empty" style={{ fontSize: 12 }}>
                  Nenhuma fonte cadastrada. O arquivo pertence a uma fonte: cadastre a fonte
                  primeiro e depois anexe o original aqui.
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {fontes.map((f) => {
                  const a = classificarAbrangencia(f.escopo, bairros, bairrosCarregando);
                  const ativa = f.id === fonteSel;
                  return (
                    <div
                      key={f.id}
                      data-testid={`fonte-${f.id}`}
                      onClick={() => setFonteSel(ativa ? null : f.id)}
                      style={{
                        fontSize: 12,
                        padding: '6px 4px',
                        borderBottom: '1px solid var(--line)',
                        display: 'flex',
                        gap: 6,
                        alignItems: 'center',
                        cursor: 'pointer',
                        background: ativa ? 'var(--warn-soft)' : undefined,
                      }}
                    >
                      <span
                        className="dot"
                        style={{ background: `var(--${a.nivel})` }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <b>{f.nome}</b>
                        <div style={{ color: 'var(--ink3)' }}>{a.uso}</div>
                      </div>
                      <span className={`chip ${a.nivel}`} style={{ fontSize: 10, height: 20 }}>
                        {a.rotulo}
                      </span>
                    </div>
                  );
                })}
              </div>

              {fonteAtiva && (
                <Anexos
                  donoTipo="fonte"
                  donoId={fonteAtiva.id}
                  tipoPadrao="Plano / programa"
                  titulo={`Arquivos desta fonte · ${fonteAtiva.nome}`}
                  ajuda="O original fica guardado e disponível para download. Serve de prova do que a fonte afirma."
                  aoAnexar={(anexo) => registrarAnexo(fonteAtiva, anexo)}
                />
              )}

              <div className="note warn" style={{ fontSize: 12, marginTop: 12 }}>
                <b>O anexo não é interpretado.</b> Bandeiras, valores, tom e público são só o
                que você escreve aqui.
                <Info titulo="Causa raiz de o anexo não ser interpretado">
                  Não existe extração de texto no servidor, nenhum leitor de PDF, DOCX ou áudio, e
                  o serviço de IA recebe apenas texto já gravado no banco. Nada é deduzido do nome
                  nem do conteúdo do arquivo.
                </Info>
              </div>
            </div>

            {/*
              O que esta nota podia dizer e o que não podia: bandeiras, valores e
              limites entram no texto do pedido ao modelo em
              `estrategias.service.ts` ("Limites que o mandato não ultrapassa:
              ..."), o que é instrução ao modelo, não bloqueio. O bloqueio
              (`validateSafetyFilter`, rotulado RN-IA-01) percorre uma lista fixa
              de termos e não lê a persona — e roda só na geração de estratégias,
              não em régua nem em campanha.
            */}
            <div className="note" style={{ fontSize: 12 }}>
              <b>A persona instrui o modelo, não o bloqueia.</b> Tom, público, bandeiras, valores
              e limites entram no pedido quando você gera uma estratégia, e o modelo pode não
              obedecer.
              <Info titulo="Qual é o bloqueio automático, e onde ele vale">
                O bloqueio é outra coisa: em Estratégias, o tema pedido e o texto gerado passam
                por uma lista fixa de termos de defeso eleitoral (RN-IA-01), que não consulta os
                seus limites. Régua de relacionamento e campanhas não passam por esse filtro.
              </Info>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
